import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { get, type Repo, type RepoList as RepoPage } from '../../services/api';
import { RepoList } from '../../components/RepoList';
import { ErrorBox, Loading, PageHeader, Pagination } from '../../components/Ui';
import { Icon } from '../../components/Icon';
import { useAsync, useTitle } from '../../hooks';

export function YourWikis() {
  useTitle('Your wikis');
  const result = useAsync(() => get<{ items: Repo[] }>('/user/repositories'), []);
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const scope = params.get('scope') ?? 'all';
  const items =
    result.data?.items.filter(
      (wiki) =>
        (scope === 'all' || (scope === 'owned' ? wiki.role === 'owner' : wiki.role !== 'owner')) &&
        `${wiki.fullName} ${wiki.description}`.toLowerCase().includes(q.toLowerCase()),
    ) ?? [];
  const update = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: key === 'q' });
  };
  return (
    <div className="container medium stack">
      <PageHeader
        title="Your wikis"
        description="Wikis you own and wikis shared with you."
        actions={
          <Link className="btn primary" to="/explore/guide">
            <Icon name="plus" />
            Share a local wiki
          </Link>
        }
      />
      <div className="filter-bar explore-toolbar">
        <div className="field">
          <label htmlFor="your-q">Find a wiki</label>
          <input
            id="your-q"
            className="input"
            type="search"
            value={q}
            onChange={(event) => update('q', event.target.value)}
            placeholder="Name or description"
          />
        </div>
        <div className="field">
          <label htmlFor="your-scope">Access</label>
          <select
            id="your-scope"
            className="input"
            value={scope}
            onChange={(event) => update('scope', event.target.value)}
          >
            <option value="all">All your wikis</option>
            <option value="owned">Owned by you</option>
            <option value="shared">Shared with you</option>
          </select>
        </div>
      </div>
      {result.loading && !result.data ? (
        <Loading />
      ) : result.error ? (
        <ErrorBox error={result.error} retry={result.reload} />
      ) : (
        <>
          <p className="muted" role="status">
            {items.length} of {result.data?.items.length} wikis
          </p>
          <div className="card">
            <RepoList
              repos={items}
              empty={q || scope !== 'all' ? 'No wikis match your filters' : 'Share your first local wiki'}
            />
          </div>
          {!result.data?.items.length && (
            <p>
              <Link to="/explore/guide">Learn how to sync your local wiki</Link> or{' '}
              <Link to="/explore/guide">connect your local wiki</Link>.
            </p>
          )}
        </>
      )}
    </div>
  );
}

export function StarredWikis() {
  useTitle('Starred wikis');
  const [params, setParams] = useSearchParams();
  const page = Math.min(10000, Math.max(1, Math.floor(Number(params.get('page')) || 1)));
  const result = useAsync(() => get<RepoPage>(`/user/starred?page=${page}&perPage=20`), [page]);
  const [message, setMessage] = useState('');
  return (
    <div className="container medium stack">
      <PageHeader
        title="Starred wikis"
        description="Saved for easy access, with your most recent stars first."
        actions={
          <Link className="btn" to="/explore">
            <Icon name="search" />
            Explore wikis
          </Link>
        }
      />
      {message && (
        <div className="alert ok" role="status">
          {message}
        </div>
      )}
      {result.loading && !result.data ? (
        <Loading />
      ) : result.error ? (
        <ErrorBox error={result.error} retry={result.reload} />
      ) : (
        result.data && (
          <>
            <p className="muted">{result.data.total} starred wikis</p>
            <div className="card">
              <RepoList
                repos={result.data.items}
                empty="Your saved reading starts here"
                onStarChange={() => {
                  setMessage('Your starred wikis have been updated.');
                  result.reload();
                }}
              />
            </div>
            {!result.data.total && (
              <p>
                Star a wiki while browsing to find it here. <Link to="/explore">Explore wikis</Link>.
              </p>
            )}
            <Pagination
              page={result.data.page}
              perPage={result.data.perPage}
              total={result.data.total}
              onPage={(next) => setParams({ page: String(next) })}
            />
          </>
        )
      )}
    </div>
  );
}
